/**
 * Backtest tab: run one, then read the equity curve and the statistics.
 *
 * The stat row deliberately leads with win rate, average R and profit
 * factor together -- a 45% win rate with +0.4R average is a good system and
 * a 70% win rate with -0.2R average is a bad one, and showing either number
 * alone invites the wrong conclusion.
 */

import { useEffect, useState } from 'react'

import type { BacktestRun, Horizon } from '@/api/types'
import { useBacktestDetail, useBacktestRuns, useRunBacktest } from '@/hooks/useQueries'
import { useAppStore } from '@/store/useAppStore'
import { TIMEFRAME_HORIZON } from '@/theme/chart'
import { EquityCurve } from './EquityCurve'

export function BacktestPanel({ ticker }: { ticker: string | null }) {
  const timeframe = useAppStore((s) => s.timeframe)
  const [selectedRun, setSelectedRun] = useState<number | null>(null)

  const runsQuery = useBacktestRuns(ticker ?? undefined)
  const detailQuery = useBacktestDetail(selectedRun)
  const runBacktest = useRunBacktest()

  // Follow the newest run for this symbol unless the user picked one.
  useEffect(() => {
    if (selectedRun === null && runsQuery.data?.length) setSelectedRun(runsQuery.data[0].id)
  }, [runsQuery.data, selectedRun])

  const horizon = (TIMEFRAME_HORIZON[timeframe] ?? 'swing') as Horizon
  const detail = detailQuery.data

  return (
    <div className="flex h-full">
      <div className="flex w-56 shrink-0 flex-col border-r border-border">
        <div className="border-b border-border p-2">
          <button
            disabled={!ticker || runBacktest.isPending}
            onClick={() =>
              ticker &&
              runBacktest.mutate(
                { ticker, horizon },
                { onSuccess: (result) => setSelectedRun(result.id) },
              )
            }
            className="w-full rounded bg-accent px-2 py-1.5 text-2xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {runBacktest.isPending ? 'Đang chạy…' : `Chạy backtest ${ticker ?? ''} ${horizon}`}
          </button>
          {runBacktest.isError && (
            <p className="mt-1 text-2xs text-down">{(runBacktest.error as Error).message}</p>
          )}
          <p className="mt-1 text-2xs text-ink-dim">
            Dùng chính pipeline live, replay trên nến đã đóng.
          </p>
        </div>

        <ul className="min-h-0 flex-1 overflow-y-auto">
          {runsQuery.data?.map((run) => (
            <li key={run.id}>
              <button
                onClick={() => setSelectedRun(run.id)}
                className={`w-full px-2 py-1.5 text-left text-2xs ${
                  run.id === selectedRun ? 'bg-accent/15' : 'hover:bg-panel-hover'
                }`}
              >
                <span className="block text-ink">
                  {run.ticker} · {run.timeframe} · {run.horizon}
                </span>
                <span className="block text-ink-dim">
                  {run.trades} lệnh · {run.winrate?.toFixed(0) ?? '—'}% ·{' '}
                  <span className={(run.avg_rr ?? 0) >= 0 ? 'text-up' : 'text-down'}>
                    {(run.avg_rr ?? 0) >= 0 ? '+' : ''}{run.avg_rr?.toFixed(2) ?? '—'}R
                  </span>
                </span>
              </button>
            </li>
          ))}
          {!runsQuery.data?.length && (
            <li className="p-2 text-2xs text-ink-dim">Chưa có lần chạy nào</li>
          )}
        </ul>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {detail ? (
          <>
            <StatRow run={detail} />
            <div className="min-h-0 flex-1">
              <EquityCurve points={detail.equity} />
            </div>
          </>
        ) : (
          <div className="grid h-full place-items-center text-2xs text-ink-dim">
            Chọn hoặc chạy một backtest
          </div>
        )}
      </div>
    </div>
  )
}

function StatRow({ run }: { run: BacktestRun }) {
  const stats: { label: string; value: string; tone?: string }[] = [
    { label: 'Số lệnh', value: String(run.trades) },
    { label: 'Tỷ lệ thắng', value: run.winrate !== null ? `${run.winrate.toFixed(1)}%` : '—' },
    {
      label: 'R trung bình',
      value: run.avg_rr !== null ? `${run.avg_rr >= 0 ? '+' : ''}${run.avg_rr.toFixed(2)}R` : '—',
      tone: (run.avg_rr ?? 0) >= 0 ? 'text-up' : 'text-down',
    },
    { label: 'Profit factor', value: run.profit_factor?.toFixed(2) ?? '—' },
    {
      label: 'Sụt giảm tối đa',
      value: run.max_drawdown !== null ? `${run.max_drawdown.toFixed(2)}R` : '—',
      tone: 'text-down',
    },
    { label: 'Sharpe', value: run.sharpe?.toFixed(2) ?? '—' },
  ]

  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-border px-3 py-2">
      {stats.map((stat) => (
        <div key={stat.label}>
          <span className="block text-2xs text-ink-dim">{stat.label}</span>
          <span className={`font-mono text-sm ${stat.tone ?? 'text-ink'}`}>{stat.value}</span>
        </div>
      ))}
      <div className="ml-auto text-right text-2xs text-ink-dim">
        <span className="block">{run.period_start} → {run.period_end}</span>
        <span className="block">
          engine {run.engine_version}
          {typeof run.params.cost_bps === 'number' && ` · phí ${run.params.cost_bps}bps/chiều`}
        </span>
      </div>
    </div>
  )
}
